/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { CellEditRequestEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import assert from "assert";
import { TOOL_EDITOR_DEFAULT_COL_DEF } from "components/ToolEditor/constants";
import { ToolEditorStoreState } from "components/ToolEditor/Provider/store/ToolEditorProvider.store";
import {
  ParamsEditorRowDatum,
  CellEditRequestValue,
} from "components/ToolEditor/ToolEditor.types";
import { useCallback, useMemo } from "react";
import { isToolParamV3 } from "types/json-schemas/type_guards";

const detailRendererStyle = css`
  width: 100%;
  .param-key {
    font-weight: 600;
  }
  min-height: 0px;
`;

type DetailRowDatum = {
  id: string;
  min: number | undefined;
  max: number | undefined;
};

type DetailColDef = ColDef<DetailRowDatum> & {
  colId: "min" | "max";
};

interface DetailRendererNumParamProps {
  type: "ToolFloatRangeParam" | "ToolIntRangeParam";
  data: ParamsEditorRowDatum;
  updateSpecParam?: ToolEditorStoreState["updateSpecParam"];
  editMode?: boolean;
}

export const DetailRendererNumParam = ({
  data,
  type,
  updateSpecParam,
  editMode = true,
}: DetailRendererNumParamProps) => {
  if (
    !isToolParamV3(data, "ToolFloatRangeParam") &&
    !isToolParamV3(data, "ToolIntRangeParam")
  ) {
    throw Error("Unexpected state");
  }

  const rowData = useMemo(() => {
    return [{ id: data.key, min: data.type.min, max: data.type.max }];
  }, [data.key, data.type.max, data.type.min]);

  const cellEditorParams = useMemo(() => {
    const baseParams: Record<string, number | boolean> = {};

    switch (type) {
      case "ToolIntRangeParam": {
        baseParams.showStepperButtons = true;
        baseParams.step = 1;
        baseParams.precision = 0;
        break;
      }
      case "ToolFloatRangeParam": {
        break;
      }
    }

    return baseParams;
  }, [type]);

  const columnDefs: DetailColDef[] = useMemo(() => {
    return [
      {
        colId: "min" as const,
        headerName: "Minimum",
        cellEditor: "agNumberCellEditor",
        cellEditorParams,
        editable: editMode,
        valueGetter: ({ data }) => data?.min,
      },
      {
        colId: "max" as const,
        headerName: "Maximum",
        cellEditor: "agNumberCellEditor",
        cellEditorParams,
        editable: editMode,
        valueGetter: ({ data }) => data?.max,
      },
    ];
  }, [cellEditorParams, editMode]);

  const getRowId = useCallback(
    ({ data }: { data: (typeof rowData)[number] }) => data.id,
    [],
  );

  const handleCellEditRequest = useCallback(
    (e: CellEditRequestEvent<(typeof rowData)[number]>) => {
      assert(updateSpecParam, "updateSpecParam is not defined");
      const colDef = e.colDef as DetailColDef;
      const colId = colDef.colId;
      const newValue = e.newValue as CellEditRequestValue;
      const sanitizedValue = (() => {
        if (typeof newValue !== "number") {
          return undefined;
        }
        switch (type) {
          case "ToolFloatRangeParam":
            return parseFloat(String(newValue));
          case "ToolIntRangeParam":
            return parseInt(String(newValue));
        }
      })();
      const paramKey = data.key;
      updateSpecParam(paramKey, type, { [colId]: sanitizedValue });
    },
    [data.key, type, updateSpecParam],
  );

  return (
    <AgGridReact<(typeof rowData)[number]>
      className="ag-theme-balham-cell-borders"
      css={detailRendererStyle}
      getRowId={getRowId}
      domLayout="autoHeight"
      columnDefs={columnDefs}
      rowData={rowData}
      readOnlyEdit
      onCellEditRequest={editMode ? handleCellEditRequest : undefined}
      defaultColDef={TOOL_EDITOR_DEFAULT_COL_DEF}
    />
  );
};
